import { useState, useEffect, useCallback, useMemo } from 'react'
import type { AssignmentStepData } from '../../types/program-scheduling.types'
import type { Group, SubscriptionTier, Client } from '../../types/database.types'
import { groupService } from '../../services'
import { useAuthStore, useClientStore } from '../../stores'
import { ExistingAssignmentsList } from './ExistingAssignmentsList'
import { QuickAddClientModal } from '../client'

interface GroupWithMembers extends Group {
  member_count?: number
}

interface AssignmentStepProps {
  data: AssignmentStepData
  onChange: (data: AssignmentStepData) => void
  onLoadGroups?: (groups: GroupWithMembers[]) => void
  // Edit mode props
  programId?: string
  providerId?: string
  isEditMode?: boolean
}

export function AssignmentStep({
  data,
  onChange,
  onLoadGroups,
  programId,
  providerId,
  isEditMode = false,
}: AssignmentStepProps) {
  const { profile } = useAuthStore()
  const { clients, fetchClients, isLoading: clientsLoading } = useClientStore()
  const [groups, setGroups] = useState<GroupWithMembers[]>([])
  const [isLoadingGroups, setIsLoadingGroups] = useState(false)
  const [clientSearch, setClientSearch] = useState('')
  const [showGroupDropdown, setShowGroupDropdown] = useState(false)
  const [alreadyAssignedClientIds, setAlreadyAssignedClientIds] = useState<Set<string>>(new Set())
  const [showQuickAddModal, setShowQuickAddModal] = useState(false)

  const providerTier = profile?.subscription_tier as SubscriptionTier | undefined
  const hasPaidTier = providerTier && providerTier !== 'free'

  // Load clients on mount
  useEffect(() => {
    if (profile?.id) {
      fetchClients(profile.id)
    }
  }, [profile?.id, fetchClients])

  // Load groups on mount (only for paid tiers)
  useEffect(() => {
    const loadGroups = async () => {
      if (!profile?.id || !hasPaidTier) return
      setIsLoadingGroups(true)
      try {
        const groupsWithCounts = await groupService.getGroupsWithCounts(profile.id)
        setGroups(groupsWithCounts)
        onLoadGroups?.(groupsWithCounts)
      } catch (err) {
        console.error('Failed to load groups:', err)
      } finally {
        setIsLoadingGroups(false)
      }
    }
    loadGroups()
  }, [profile?.id, hasPaidTier, onLoadGroups])

  // Handle callback from ExistingAssignmentsList
  const handleAssignedClientsLoaded = useCallback((clientIds: string[]) => {
    setAlreadyAssignedClientIds(new Set(clientIds))
  }, [])

  // Toggle client selection
  const toggleClientSelection = useCallback((clientId: string) => {
    const newSelected = new Set(data.selectedClientIds)
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId)
    } else {
      newSelected.add(clientId)
    }
    onChange({
      ...data,
      mode: newSelected.size > 0 ? 'multiple' : 'skip',
      selectedClientIds: newSelected,
    })
  }, [data, onChange])

  // Remove a selected client (from chip)
  const removeClient = useCallback((clientId: string) => {
    const newSelected = new Set(data.selectedClientIds)
    newSelected.delete(clientId)
    onChange({
      ...data,
      mode: newSelected.size > 0 ? 'multiple' : 'skip',
      selectedClientIds: newSelected,
    })
  }, [data, onChange])

  // Add all members from a group
  const addGroupMembers = useCallback(async (groupId: string) => {
    try {
      const memberIds = await groupService.getGroupMemberIds(groupId)
      // Filter out already assigned clients
      const newIds = memberIds.filter(id => !alreadyAssignedClientIds.has(id))
      const newSelected = new Set([...data.selectedClientIds, ...newIds])
      onChange({
        ...data,
        mode: newSelected.size > 0 ? 'multiple' : 'skip',
        selectedClientIds: newSelected,
        groupId, // Track which group was used
      })
      setShowGroupDropdown(false)
    } catch (err) {
      console.error('Failed to load group members:', err)
    }
  }, [data, onChange, alreadyAssignedClientIds])

  const handleNotesChange = useCallback((notes: string) => {
    onChange({
      ...data,
      notes,
    })
  }, [data, onChange])

  // Handle new client created from quick add modal - auto-select them
  const handleQuickAddClient = useCallback((newClient: Client) => {
    const newSelected = new Set(data.selectedClientIds)
    newSelected.add(newClient.id)
    onChange({
      ...data,
      mode: 'multiple',
      selectedClientIds: newSelected,
    })
    setClientSearch('')
    setShowQuickAddModal(false)
  }, [data, onChange])

  // Filter active clients, excluding already assigned
  const availableClients = useMemo(() => {
    const activeClients = clients.filter(
      (c) => (c.status === 'active' || c.status === 'invited') && !alreadyAssignedClientIds.has(c.id)
    )
    if (clientSearch) {
      return activeClients.filter(
        (c) =>
          c.full_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
          c.email.toLowerCase().includes(clientSearch.toLowerCase())
      )
    }
    return activeClients
  }, [clients, clientSearch, alreadyAssignedClientIds])

  // Get selected client details for chips
  const selectedClients = useMemo(() => {
    return clients.filter((c) => data.selectedClientIds.has(c.id))
  }, [clients, data.selectedClientIds])

  const selectAllAvailable = () => {
    const allAvailableIds = availableClients.map((c) => c.id)
    const newSelected = new Set([...data.selectedClientIds, ...allAvailableIds])
    onChange({
      ...data,
      mode: newSelected.size > 0 ? 'multiple' : 'skip',
      selectedClientIds: newSelected,
    })
  }

  const clearSelection = () => {
    onChange({
      ...data,
      mode: 'skip',
      selectedClientIds: new Set(),
    })
  }

  // Access duration is now tied to program duration (set in Program Basics step)

  // Use providerId from props or fall back to profile.id
  const effectiveProviderId = providerId || profile?.id

  return (
    <div className="space-y-6">
      {/* Existing Assignments Section (edit mode only) */}
      {isEditMode && programId && effectiveProviderId && (
        <>
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Current Assignments
            </h3>
            <ExistingAssignmentsList
              programId={programId}
              providerId={effectiveProviderId}
              maxVisible={5}
              onAssignedClientsLoaded={handleAssignedClientsLoaded}
            />
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-sm font-medium text-gray-900 mb-1">
              Add More Clients
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Select additional clients to assign this program to
            </p>
          </div>
        </>
      )}

      {/* Create mode header */}
      {!isEditMode && (
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">
            Assign to Clients
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Select clients to assign this program to, or skip to assign later
          </p>
        </div>
      )}

      {/* Selected Clients Chips */}
      {selectedClients.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Selected ({selectedClients.length})
            </label>
            <button
              type="button"
              onClick={clearSelection}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedClients.map((client) => (
              <span
                key={client.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
              >
                {client.full_name}
                <button
                  type="button"
                  onClick={() => removeClient(client.id)}
                  className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Client Selection */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            {isEditMode ? 'Available Clients' : 'Select Clients'}
          </label>
          <div className="flex items-center gap-3">
            {/* Add New Client button */}
            <button
              type="button"
              onClick={() => setShowQuickAddModal(true)}
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add New
            </button>
            {availableClients.length > 0 && (
              <>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={selectAllAvailable}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Select All
                </button>
              </>
            )}
            {/* Add from Group dropdown (paid tiers only) */}
            {hasPaidTier && groups.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Add from Group
                  <svg className={`w-4 h-4 transition-transform ${showGroupDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showGroupDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <div className="py-1 max-h-48 overflow-y-auto">
                      {isLoadingGroups ? (
                        <div className="px-4 py-2 text-sm text-gray-500">Loading groups...</div>
                      ) : (
                        groups.map((group) => (
                          <button
                            key={group.id}
                            type="button"
                            onClick={() => addGroupMembers(group.id)}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between"
                          >
                            <span className="font-medium text-gray-900">{group.name}</span>
                            <span className="text-gray-500">{group.member_count || 0} members</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search clients..."
          value={clientSearch}
          onChange={(e) => setClientSearch(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2"
        />

        {/* Client List */}
        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
          {clientsLoading ? (
            <div className="p-4 text-center text-gray-500">Loading clients...</div>
          ) : availableClients.length === 0 ? (
            <div className="p-4 text-center">
              {clientSearch ? (
                <>
                  <p className="text-gray-500 mb-2">No clients match "{clientSearch}"</p>
                  <button
                    type="button"
                    onClick={() => setShowQuickAddModal(true)}
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create "{clientSearch}" as a new client
                  </button>
                </>
              ) : alreadyAssignedClientIds.size > 0 ? (
                <p className="text-gray-500">All clients are already assigned to this program</p>
              ) : (
                <>
                  <p className="text-gray-500 mb-2">No active clients available</p>
                  <button
                    type="button"
                    onClick={() => setShowQuickAddModal(true)}
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add your first client
                  </button>
                </>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {availableClients.map((client) => {
                const isSelected = data.selectedClientIds.has(client.id)
                return (
                  <li
                    key={client.id}
                    onClick={() => toggleClientSelection(client.id)}
                    className={`p-3 cursor-pointer hover:bg-gray-50 flex items-center gap-3 ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleClientSelection(client.id)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium text-gray-600">
                        {client.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {client.full_name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{client.email}</p>
                    </div>
                    {isSelected && (
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* Already assigned hint */}
        {isEditMode && alreadyAssignedClientIds.size > 0 && (
          <p className="mt-2 text-sm text-gray-500">
            {alreadyAssignedClientIds.size} client{alreadyAssignedClientIds.size !== 1 ? 's' : ''} already assigned (shown above)
          </p>
        )}
      </div>

      {/* Notes */}
      {data.selectedClientIds.size > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes for Client(s) <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            value={data.notes}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Instructions or notes for your client(s)..."
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
          <p className="mt-1 text-sm text-gray-500">
            These notes will be visible to the client(s)
          </p>
        </div>
      )}

      {/* Access Duration Info (shown when clients selected) */}
      {data.selectedClientIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-1">Access Duration</h4>
          <p className="text-sm text-blue-700">
            Assigned clients will have access for the{' '}
            <span className="font-medium">duration set in Program Basics</span>.
          </p>
        </div>
      )}

      {/* Skip info (shown when no clients selected) */}
      {data.selectedClientIds.size === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="font-medium text-gray-900">No clients selected</h4>
              <p className="text-sm text-gray-600 mt-1">
                {isEditMode
                  ? 'Select clients above to add new assignments, or continue without adding any.'
                  : 'You can assign clients to this program later from the program details page or the Assignments section.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Client Modal */}
      <QuickAddClientModal
        isOpen={showQuickAddModal}
        onClose={() => setShowQuickAddModal(false)}
        onClientCreated={handleQuickAddClient}
        providerId={profile?.id || ''}
        providerName={profile?.full_name || 'Your Provider'}
        initialSearchQuery={clientSearch}
      />
    </div>
  )
}
